import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Info, Star } from "lucide-react";
import { WatchNowButton } from "./WatchNowButton";
import { PlayTrailerButton } from "./TrailerButtons";
import { ErrorState } from "./ErrorState";
import { backdropUrl, year } from "../lib/api";

const HeroSkeleton = () => (
  <div className="relative h-[78vh] min-h-[540px] w-full bg-[#0f0f0f]">
    <div className="absolute bottom-24 left-5 max-w-xl space-y-4 md:left-10 lg:left-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))]">
      <div className="skeleton h-3 w-32 rounded" />
      <div className="skeleton h-14 w-96 max-w-[80vw] rounded" />
      <div className="skeleton h-20 w-[32rem] max-w-[85vw] rounded" />
      <div className="skeleton h-12 w-64 rounded-full" />
    </div>
  </div>
);

export const HeroCarousel = ({ movies, loading, error, onRetry }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const items = (movies || []).filter((m) => m.backdrop_path).slice(0, 5);

  useEffect(() => {
    if (paused || items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 7000);
    return () => clearInterval(t);
  }, [paused, items.length]);

  if (loading) return <HeroSkeleton />;
  if (error)
    return (
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-32 md:px-10">
        <ErrorState message={error} onRetry={onRetry} testId="hero-error" />
      </div>
    );
  if (!items.length) return null;
  const m = items[idx];

  return (
    <section
      data-testid="hero-banner"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative h-[82vh] min-h-[560px] w-full overflow-hidden"
    >
      {items.map((it, i) => (
        <img
          key={it.id}
          src={backdropUrl(it.backdrop_path, "original")}
          alt=""
          className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] duration-[1400ms] ease-out ${
            i === idx ? "scale-100 opacity-100" : "scale-105 opacity-0"
          }`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-black/40" />
      <div className="grain absolute inset-0" />

      <div className="relative mx-auto flex h-full max-w-7xl items-end px-5 pb-20 md:px-10 md:pb-28">
        <div key={m.id} className="hero-copy max-w-2xl">
          <p className="eyebrow text-[#D4AF37]">Trending Today · No. {idx + 1}</p>
          <h1 data-testid="hero-title" className="mt-4 font-display text-4xl font-bold leading-[1.05] text-white sm:text-5xl lg:text-6xl">
            {m.title}
          </h1>
          <div className="mt-5 flex items-center gap-4 text-sm text-neutral-300">
            <span className="flex items-center gap-1.5 text-[#D4AF37]">
              <Star className="h-4 w-4 fill-current" /> {m.vote_average?.toFixed(1)}
            </span>
            <span className="h-1 w-1 rounded-full bg-neutral-500" />
            <span>{year(m.release_date)}</span>
          </div>
          <p className="mt-5 line-clamp-3 max-w-xl text-sm leading-relaxed text-neutral-300 md:text-base">{m.overview}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <WatchNowButton item={m} testId="hero-watch-now-btn" />
            <PlayTrailerButton item={m} />
            <Link
              to={`/movie/${m.id}`}
              data-testid="hero-more-info-btn"
              className="btn-ghost inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-medium uppercase tracking-[0.14em]"
            >
              <Info className="h-4 w-4" /> More Info
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 right-5 flex gap-2 md:right-10">
        {items.map((it, i) => (
          <button
            key={it.id}
            type="button"
            onClick={() => setIdx(i)}
            data-testid={`hero-dot-${i}`}
            aria-label={`Show ${it.title}`}
            className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${
              i === idx ? "w-10 bg-[#D4AF37]" : "w-4 bg-white/30 hover:bg-white/60"
            }`}
          />
        ))}
      </div>
    </section>
  );
};
